import { useTranslation } from 'react-i18next';

/**
 * Per-widget reload control rendered in the WidgetContainer title bar
 * (Phase 10 step 3). Wired to `useQueryResult().reload`, which evicts
 * the worker's LRU entry for the dataset and bumps the hook's local
 * dispatch key so the cached-result short-circuit misses once.
 *
 * Mirrors the visual weight of ChartExportButton (compact pill, muted
 * text, dotted-square refresh glyph) so the widget header stays calm.
 */
type Props = {
  onReload: () => void;
  /** Disable while a fetch is in flight to avoid pile-up. */
  disabled?: boolean;
};

export function ReloadButton({ onReload, disabled = false }: Props): JSX.Element {
  const { t } = useTranslation();
  const label = t('widgetActions.reload');
  return (
    <button
      type="button"
      onClick={onReload}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="text-xs px-1.5 py-0.5 rounded hover:opacity-80 disabled:opacity-50"
      style={{
        color: 'var(--color-muted)',
        border: '1px solid var(--color-border)',
        background: 'var(--color-bg)',
      }}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden
      >
        <path
          d="M3 8a5 5 0 0 1 8.5-3.5L13 6M13 8a5 5 0 0 1-8.5 3.5L3 10M13 3v3h-3M3 13v-3h3"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
